// ============================================================
// ADVANCE WEEKLY OCCURRENCE
// ============================================================
//
// Finds the next scheduled calendar date.
//
// Weekly rules:
//
// - One or more selected weekdays are supported.
// - No weekdays means the weekday of recurrence_start_date.
// - interval_value is the number of weeks between recurrence
//   weeks.
// - Database DATE values are interpreted as calendar dates.
// - Multi-week intervals require a recurrence start date.
// - Returned dates must be strictly after currentDate.
// ============================================================


// ------------------------------------------------------------
// Convert a database DATE into a local calendar date.
// ------------------------------------------------------------

function toLocalCalendarDate(value) {
    if (!value) {
        return null
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null
        }

        // Preserve the calendar date stored in the database.
        return new Date(
            value.getUTCFullYear(),
            value.getUTCMonth(),
            value.getUTCDate()
        )
    }

    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
        const [year, month, day] =
            value.split('-').map(Number)

        const result = new Date(
            year,
            month - 1,
            day
        )

        // Reject impossible calendar dates.
        if (
            result.getFullYear() !== year ||
            result.getMonth() !== month - 1 ||
            result.getDate() !== day
        ) {
            return null
        }

        return result
    }

    const parsed = new Date(value)

    if (Number.isNaN(parsed.getTime())) {
        return null
    }

    return new Date(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate()
    )
}


// ------------------------------------------------------------
// Normalize a runtime date to its local calendar day.
// ------------------------------------------------------------

function toCurrentCalendarDate(value) {
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null
        }

        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
        )
    }

    return toLocalCalendarDate(value)
}


// ------------------------------------------------------------
// Return the Sunday-based beginning of a calendar week.
// ------------------------------------------------------------

function startOfWeek(date) {
    const result = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    )

    result.setDate(
        result.getDate() - result.getDay()
    )

    return result
}


// ============================================================
// ADVANCE WEEKLY OCCURRENCE
// ============================================================

export function advanceWeekly({
    template,
    currentDate
}) {
    if (!template || !currentDate) {
        return null
    }

    // --------------------------------------------------------
    // Current calendar date.
    // --------------------------------------------------------

    const current =
        toCurrentCalendarDate(currentDate)

    if (!current) {
        return null
    }

    // --------------------------------------------------------
    // Recurrence start date.
    // --------------------------------------------------------

    const startDate =
        toLocalCalendarDate(
            template.recurrence_start_date
        )

    // --------------------------------------------------------
    // Weekly interval.
    // NULL or empty means every week.
    // Invalid intervals must not silently become every week.
    // --------------------------------------------------------

    const rawInterval = template.interval_value

    const interval =
        rawInterval == null || rawInterval === ''
            ? 1
            : Number(rawInterval)

    if (
        !Number.isInteger(interval) ||
        interval < 1
    ) {
        return null
    }

    // Multi-week schedules need a stable anchor.
    if (interval > 1 && !startDate) {
        return null
    }

    // --------------------------------------------------------
    // Determine scheduled weekdays.
    // --------------------------------------------------------

    let days =
        Array.isArray(template.days_of_week)
            ? template.days_of_week
                .map(Number)
                .filter(day =>
                    Number.isInteger(day) &&
                    day >= 0 &&
                    day <= 6
                )
            : []

    // --------------------------------------------------------
    // No weekdays selected:
    // repeat on the weekday of the start date.
    // --------------------------------------------------------

    if (days.length === 0) {
        if (!startDate) {
            return null
        }

        days = [startDate.getDay()]
    }

    // Remove duplicates and sort.
    days = [...new Set(days)].sort(
        (a, b) => a - b
    )

    // --------------------------------------------------------
    // Anchor recurrence to the week containing the start date.
    //
    // For an every-week schedule without a start date, use the
    // current week as the temporary anchor.
    // --------------------------------------------------------

    const startWeek = startOfWeek(
        startDate || current
    )

    // --------------------------------------------------------
    // Search forward for the next eligible weekday.
    // --------------------------------------------------------

    const searchDays = (interval * 7) + 7

    for (let i = 1; i <= searchDays; i++) {
        const candidate = new Date(
            current.getFullYear(),
            current.getMonth(),
            current.getDate()
        )

        candidate.setDate(
            candidate.getDate() + i
        )

        // Never schedule before the recurrence start date.
        if (startDate && candidate < startDate) {
            continue
        }

        // Is this one of the selected weekdays?
        if (!days.includes(candidate.getDay())) {
            continue
        }

        // Determine the candidate's calendar week.
        const candidateWeek =
            startOfWeek(candidate)

        const daysBetween =
            Math.round(
                (
                    candidateWeek.getTime() -
                    startWeek.getTime()
                ) /
                (24 * 60 * 60 * 1000)
            )

        const weeksSinceStart =
            Math.floor(daysBetween / 7)

        // Is this an eligible recurrence week?
        if (
            weeksSinceStart >= 0 &&
            weeksSinceStart % interval === 0
        ) {
            return candidate
        }
    }

    return null
}